import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, LocateFixed, Loader2, Check, Search } from 'lucide-react'
import { Button } from './ui/button'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../lib/utils'

// Settings → Briefing. Where the morning voice briefing gets its
// shape: the city the weather is for (search by name via open-meteo
// geocoding, or one-tap GPS), which sections to include, and the news
// interests the AI uses to pick headlines. Persists to
// user_profiles.briefing_prefs (migration_126).

const SECTION_KEYS = ['weather', 'news', 'finance', 'training', 'habits', 'goals']
const DEFAULT_SECTIONS = Object.fromEntries(SECTION_KEYS.map((k) => [k, true]))

export default function BriefingSettings() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const isAr = i18n.language === 'ar'

  const [prefs, setPrefs] = useState({ city: null, lat: null, lng: null, sections: DEFAULT_SECTIONS, interests: [] })
  const [loaded, setLoaded] = useState(false)
  const [cityQuery, setCityQuery] = useState('')
  const [cityResults, setCityResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [locating, setLocating] = useState(false)
  const [interestsText, setInterestsText] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedTick, setSavedTick] = useState(false)

  useEffect(() => {
    if (!user) return undefined
    let alive = true
    supabase
      .from('user_profiles')
      .select('briefing_prefs')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!alive) return
        const p = data?.briefing_prefs || {}
        setPrefs({
          city: p.city || null,
          lat: p.lat ?? null,
          lng: p.lng ?? null,
          sections: { ...DEFAULT_SECTIONS, ...(p.sections || {}) },
          interests: Array.isArray(p.interests) ? p.interests : [],
        })
        setInterestsText((Array.isArray(p.interests) ? p.interests : []).join('، '))
        setLoaded(true)
      })
    return () => { alive = false }
  }, [user])

  const save = async (next) => {
    const merged = { ...prefs, ...next }
    setPrefs(merged)
    setSaving(true)
    await supabase
      .from('user_profiles')
      .upsert({ user_id: user.id, briefing_prefs: merged, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
    setSaving(false)
    setSavedTick(true)
    setTimeout(() => setSavedTick(false), 1500)
  }

  const searchCity = async (e) => {
    e.preventDefault()
    const q = cityQuery.trim()
    if (!q || searching) return
    setSearching(true)
    try {
      const lang = isAr ? 'ar' : 'en'
      const resp = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}&count=5&language=${lang}`)
      const data = await resp.json()
      setCityResults(data?.results || [])
    } catch {
      setCityResults([])
    }
    setSearching(false)
  }

  const pickCity = (r) => {
    setCityResults([])
    setCityQuery('')
    save({ city: r.name, lat: r.latitude, lng: r.longitude })
  }

  const useGps = () => {
    if (!navigator.geolocation || locating) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        save({
          city: t('settings.briefing.myLocation'),
          lat: Math.round(pos.coords.latitude * 100) / 100,
          lng: Math.round(pos.coords.longitude * 100) / 100,
        })
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 10000 },
    )
  }

  const toggleSection = (key) => {
    save({ sections: { ...prefs.sections, [key]: !prefs.sections[key] } })
  }

  const saveInterests = () => {
    const interests = interestsText
      .split(/[,،\n]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 10)
    save({ interests })
  }

  if (!loaded) return null

  return (
    <div className="p-3 sm:p-4 space-y-4">
      {/* ── Location ──────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <p className="flex-1 min-w-0 text-sm font-medium text-foreground flex items-center gap-1.5" dir="auto">
            <MapPin className="w-4 h-4 text-app-primary-500" />
            {t('settings.briefing.location')}
          </p>
          {savedTick && <Check className="w-4 h-4 text-emerald-500" />}
          {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>
        {prefs.city && (
          <p className="text-xs text-muted-foreground" dir="auto">
            {t('settings.briefing.current')} <b className="text-foreground">{prefs.city}</b>
            {prefs.lat != null && <span dir="ltr"> ({prefs.lat}, {prefs.lng})</span>}
          </p>
        )}
        <form onSubmit={searchCity} className="flex gap-2">
          <input
            type="text"
            value={cityQuery}
            onChange={(e) => setCityQuery(e.target.value)}
            placeholder={t('settings.briefing.cityPlaceholder')}
            className="h-10 flex-1 min-w-0 px-3 rounded-md border border-input bg-background text-foreground text-sm"
            dir="auto"
          />
          <Button type="submit" size="sm" variant="outline" disabled={searching || !cityQuery.trim()} className="h-10">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={useGps} disabled={locating} className="h-10" title={t('settings.briefing.useGps')}>
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
          </Button>
        </form>
        {cityResults.length > 0 && (
          <div className="rounded-md border border-border divide-y divide-border/60">
            {cityResults.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => pickCity(r)}
                className="w-full px-3 py-2 text-start text-sm text-foreground hover:bg-accent"
                dir="auto"
              >
                {r.name}
                <span className="text-xs text-muted-foreground"> · {r.admin1 || ''} {r.country || ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Sections ──────────────────────────────────────────── */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground" dir="auto">{t('settings.briefing.sectionsTitle')}</p>
        <div className="flex flex-wrap gap-1.5">
          {SECTION_KEYS.map((key) => {
            const on = prefs.sections[key] !== false
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleSection(key)}
                aria-pressed={on}
                className={cn(
                  'px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors',
                  on ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground',
                )}
                dir="auto"
              >
                {t(`settings.briefing.sections.${key}`)}
              </button>
            )
          })}
        </div>
      </div>

      {/* ── News interests ────────────────────────────────────── */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground" dir="auto">{t('settings.briefing.interestsTitle')}</p>
        <p className="text-[11px] text-muted-foreground" dir="auto">{t('settings.briefing.interestsHint')}</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={interestsText}
            onChange={(e) => setInterestsText(e.target.value)}
            placeholder={t('settings.briefing.interestsPlaceholder')}
            className="h-10 flex-1 min-w-0 px-3 rounded-md border border-input bg-background text-foreground text-sm"
            dir="auto"
          />
          <Button type="button" size="sm" onClick={saveInterests} disabled={saving} className="h-10">
            {t('common.save')}
          </Button>
        </div>
        {prefs.interests.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {prefs.interests.map((tag, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded bg-app-primary-500/10 text-app-primary-500 text-[10px]" dir="auto">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
